<!--
  One document in icon view.

  TASK-042 §5. Three decisions are visible in this file and each was made
  elsewhere in the ticket:

  1. **The actions are a `DocRowMenu` in the top-right corner.** That component
     survived phase 1 for exactly this: a tile is too small to carry three
     labelled buttons and the grid too dense to show them all at once, and its
     hard-won bits -- the outside-click and Escape handlers registered only
     while open, the `aria-haspopup`/`aria-expanded` pair, the per-document
     `aria-label` -- are precisely what a tile needs and would otherwise be
     rewritten. It renders from the same `docActions()` as the list's buttons,
     so the two presentations cannot drift into offering different things.

  2. **The sketch is asked for only when the tile is on screen**, through the
     shared observer in `sketchstore.svelte.ts`, and its absence never delays
     the tile: the name and the metadata are here on the first frame and the
     shape arrives afterwards, or not at all.

  3. **The first heading is shown only when it DIFFERS from the name.** For
     most documents the first heading IS the document's name, and rendering
     the name twice on one tile is noise (§5b). If it turns out to differ
     almost never, the field is deleted from the route and this block goes with
     it; it is one line either way, which is what made it worth finding out.

  The tile is a `gridcell` and the whole cell is the link. Arrow-key movement
  and the roving tabindex belong to the GRID, which is the only thing that
  knows how many columns there are, so this component takes `tabindex` as a
  prop rather than deciding it.
-->
<script lang="ts">
  import type { DocRow } from './doclist-types'
  import { sketches } from '../sketchstore.svelte'
  import DocRowMenu from './DocRowMenu.svelte'
  import DocSketch from './DocSketch.svelte'

  let {
    doc,
    archived = false,
    tabindex = -1,
    onshare,
    onrename,
    onmove,
    ondelete,
    onleave,
    onrestore,
    onkey,
  }: {
    doc: DocRow
    archived?: boolean
    /** The grid's roving tabindex: 0 on exactly one cell. */
    tabindex?: number
    onshare: (doc: DocRow) => void
    onrename: (doc: DocRow) => void
    onmove: (doc: DocRow) => void
    ondelete: (doc: DocRow) => void
    onleave: (doc: DocRow) => void
    onrestore: (doc: DocRow) => void
    onkey: (event: KeyboardEvent, doc: DocRow) => void
  } = $props()

  let cell = $state<HTMLElement | null>(null)

  const sketch = $derived(sketches.byId[doc.id])
  /** The heading is worth drawing only when it is not the name again. */
  const subtitle = $derived(
    sketch?.heading && sketch.heading.trim() !== doc.name.trim() ? sketch.heading : null,
  )

  $effect(() => {
    if (!cell) return
    return sketches.observe(cell, doc.id)
  })

  function when(iso: string): string {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
  }
</script>

<div class="tile" role="gridcell" bind:this={cell}>
  {#if archived}
    <!-- Not a link: an archived document cannot be opened, by anybody, until
         it is restored. -->
    <span class="tile-body tile-dead" {tabindex} onkeydown={(e) => onkey(e, doc)} role="link">
      <DocSketch {sketch} />
      <span class="tile-foot">
        <span class="tile-name">{doc.name}</span>
        <span class="tile-meta">
          <span>rev {doc.rev}</span>
          {#if doc.archivedAt}<span>deleted {when(doc.archivedAt)}</span>{/if}
        </span>
      </span>
    </span>
  {:else}
    <a
      class="tile-body"
      href={`/d/${doc.id}`}
      {tabindex}
      data-tile-id={doc.id}
      onkeydown={(e) => onkey(e, doc)}
    >
      <DocSketch {sketch} />
      <span class="tile-foot">
        <span class="tile-name">{doc.name}</span>
        {#if subtitle}<span class="tile-sub">{subtitle}</span>{/if}
        <span class="tile-meta">
          <span>rev {doc.rev}</span>
          <span>{when(doc.updated_at)}</span>
        </span>
      </span>
    </a>
  {/if}

  <div class="tile-menu">
    <DocRowMenu {doc} {archived} {onshare} {onrename} {onmove} {ondelete} {onleave} {onrestore} />
  </div>
</div>

<style>
  .tile {
    position: relative;
    display: flex;
    min-width: 0;
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.5rem;
    overflow: hidden;
    background: var(--color-plinth, #f1ece4);
  }

  .tile-body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    text-decoration: none;
    color: inherit;
  }

  .tile-body:hover .tile-name {
    color: var(--color-red-deep, #c01030);
  }

  .tile-dead {
    color: var(--color-ink-soft, #6e665c);
    cursor: default;
  }

  .tile-foot {
    display: block;
    padding: 0.5rem 0.55rem 0.6rem;
    min-width: 0;
  }

  .tile-name {
    font-size: 0.9rem;
    line-height: 1.2;
    font-weight: 500;
    /* Two lines, then an ellipsis: a tile whose height depends on its title
       makes the grid ragged, and a ragged grid has no reading order. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    transition: color 0.12s ease;
  }

  .tile-sub {
    display: block;
    margin-top: 0.15rem;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .tile-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.3rem;
    font-family: var(--font-mono, ui-monospace, monospace);
    font-size: 0.65rem;
    color: var(--color-ink-soft, #6e665c);
  }

  /* Top-right, which is where §3b put it and where a file browser keeps it. */
  .tile-menu {
    position: absolute;
    top: 0.15rem;
    right: 0.15rem;
    z-index: 5;
  }
</style>
